import { useMutation, useQueryClient } from "@tanstack/react-query"
import { Camera, Loader2, Plus } from "lucide-react"
import React, { useRef, useState } from "react"

import { cn, toast } from "@atlant/ui"

import { graphql } from "#gql"
import type { AircraftSource } from "#gql/graphql.ts"
import AircraftImage from "#images/aircraft-image.png"
import { queryKeys } from "#lib/query-keys.ts"
import { AircraftImageGalleryModal } from "#pages/authenticated/fleet/[aircraftId]/components/aircraft-images-view.tsx"
import { useLoggedInUserInfo } from "#store/user-info.ts"
import { getFileUrl } from "#utils/file-url.ts"
import { client } from "#utils/graphql-client.ts"

const PublicFileUploadDocument = graphql(/* GraphQL */ `
  mutation PublicFileUpload($input: MutationPublicFileUploadInput!) {
    publicFileUpload(input: $input)
  }
`)

const AircraftUpdateImagesDocument = graphql(/* GraphQL */ `
  mutation AircraftUpdateImages($input: MutationAircraftUpdateImagesInput!) {
    aircraftUpdateImages(input: $input) {
      id
      images
    }
  }
`)

interface AircraftImageUploadProps {
  source: AircraftSource
  aircraftId: string
  images: string[]
  tail: string
  manufacturer: string
  model: string
  serial: string
  yom: string
}

const MAX_FILES = 16

export const AircraftImageUpload = ({
  source,
  aircraftId,
  images,
  tail,
  manufacturer,
  model,
  serial,
  yom,
}: AircraftImageUploadProps) => {
  const { me } = useLoggedInUserInfo()
  const queryClient = useQueryClient()
  const inputRef = useRef<HTMLInputElement>(null)
  const [isUploading, setIsUploading] = useState(false)
  const [isGalleryOpen, setIsGalleryOpen] = useState(false)

  const { mutateAsync: uploadFile } = useMutation({
    mutationFn: (variables: { name: string; file: File }) =>
      client.request(PublicFileUploadDocument, {
        input: {
          organizationId: me.organization.id,
          name: variables.name,
          file: variables.file,
          entity: "aircraft",
        },
      }),
  })

  const { mutateAsync: updateImages } = useMutation({
    mutationFn: (newImages: string[]) =>
      client.request(AircraftUpdateImagesDocument, {
        input: {
          id: aircraftId,
          organizationId: me.organization.id,
          images: newImages,
          source,
        },
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.aircraft.detail(aircraftId) })
    },
  })

  const handleFiles = async (files: FileList) => {
    const allowed = MAX_FILES - images.length
    if (allowed <= 0) {
      toast.error(`Maximum ${MAX_FILES} images allowed`)
      return
    }

    const fileArray = Array.from(files)
      .filter((f) => f.type.startsWith("image/"))
      .slice(0, allowed)

    if (fileArray.length === 0) return

    setIsUploading(true)
    const uploadedUrls: string[] = []

    for (const file of fileArray) {
      try {
        const result = await uploadFile({ name: file.name, file })
        uploadedUrls.push(result.publicFileUpload)
      } catch {
        toast.error(`Failed to upload ${file.name}`)
      }
    }

    if (uploadedUrls.length > 0) {
      const newImages = [...images, ...uploadedUrls]
      await updateImages(newImages)
      toast.success(
        `${uploadedUrls.length} image${uploadedUrls.length > 1 ? "s" : ""} uploaded`,
      )
    }

    setIsUploading(false)
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      void handleFiles(e.target.files)
      e.target.value = ""
    }
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    if (e.dataTransfer.files) {
      void handleFiles(e.dataTransfer.files)
    }
  }

  const openPicker = () => inputRef.current?.click()

  const hasImages = images.length > 0
  const main = images[0]
  const thumbs = images.slice(1, 4)
  const emptySlots = 3 - thumbs.length

  return (
    <>
      <input
        ref={inputRef}
        type="file"
        accept="image/png,image/jpeg"
        multiple
        className="hidden"
        onChange={handleChange}
      />

      {hasImages ? (
        <div
          className="flex h-36 shrink-0 gap-2"
          onDrop={handleDrop}
          onDragOver={(e) => e.preventDefault()}
        >
          <button
            type="button"
            onClick={() => setIsGalleryOpen(true)}
            disabled={isUploading}
            className={cn(
              "relative h-full w-48 shrink-0 overflow-hidden rounded-xl",
              isUploading && "pointer-events-none opacity-50",
            )}
          >
            <img
              src={getFileUrl(me.organization.id, "aircraft", main)}
              alt=""
              className="size-full object-cover"
            />
            <div className="absolute right-2 bottom-2 flex items-center gap-1 rounded-full bg-black/60 px-2 py-1 text-xs text-white">
              <Camera className="size-3.5" />
              {images.length}
            </div>
            {isUploading && (
              <div className="absolute inset-0 flex items-center justify-center bg-black/40">
                <Loader2 className="size-6 animate-spin text-white" />
              </div>
            )}
          </button>

          <div className="flex h-full w-20 flex-col gap-2">
            {thumbs.map((url) => {
              return (
                <button
                  key={url}
                  type="button"
                  className="relative flex-1 overflow-hidden rounded-lg"
                >
                  <img
                    src={getFileUrl(me.organization.id, "aircraft", url)}
                    alt=""
                    className="size-full object-cover"
                  />
                </button>
              )
            })}

            {Array.from({ length: emptySlots }).map((_, i) => (
              <button
                key={`empty-${i}`}
                type="button"
                onClick={openPicker}
                className="text-muted-foreground hover:border-primary hover:text-primary flex flex-1 items-center justify-center rounded-lg border border-dashed"
              >
                <Plus className="size-4" />
              </button>
            ))}
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={openPicker}
          disabled={isUploading}
          onDrop={handleDrop}
          onDragOver={(e) => e.preventDefault()}
          className={cn(
            "bg-muted/20 text-muted-foreground flex h-36 w-48 shrink-0 flex-col items-center justify-center rounded-xl border border-dashed",
            isUploading && "pointer-events-none opacity-50",
          )}
        >
          {isUploading ? (
            <Loader2 className="text-primary mb-2 size-8 animate-spin" />
          ) : (
            <img
              src={AircraftImage}
              alt=""
              className="mb-2 h-16 object-contain opacity-60"
            />
          )}
          <span className="text-foreground text-xs font-medium">
            Upload aircraft photos
          </span>
          <span className="text-muted-foreground mt-0.5 text-[10px]">
            PNG or JPG, up to {MAX_FILES} files
          </span>
        </button>
      )}

      <AircraftImageGalleryModal
        open={isGalleryOpen}
        onClose={() => setIsGalleryOpen(false)}
        aircraftId={aircraftId}
        images={images}
        tail={tail}
        manufacturer={manufacturer}
        model={model}
        serial={serial}
        yom={yom}
      />
    </>
  )
}
